import { useState } from 'react';
import AppModal from '../common/AppModal';

interface DeletePoolModalProps {
  open: boolean;
  poolName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

// Permanent, non-cascading delete - a single confirm step, no reason needed.
const DeletePoolModal = ({
  open,
  poolName,
  onClose,
  onConfirm,
}: DeletePoolModalProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pool.');
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      maxWidthClassName="max-w-[420px]"
    >
      <div className="mb-4 text-center text-xl font-extrabold leading-tight text-dark">
        Delete &ldquo;{poolName}&rdquo;?
      </div>
      <div className="text-center text-sm font-medium text-light">
        This will permanently delete this pool. However, any associated services
        and repairs will remain in the system.
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-danger/20 bg-dangerLight px-3.5 py-2.5 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          disabled={submitting}
          onClick={handleClose}
          className="h-12 flex-1 rounded-xl border-[1.5px] border-grey bg-white text-[15px] font-semibold text-dark hover:bg-grey4 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={handleConfirm}
          className="h-12 flex-1 rounded-xl bg-danger text-[15px] font-semibold text-white hover:bg-[#b30000] disabled:cursor-not-allowed disabled:opacity-45"
        >
          {submitting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </AppModal>
  );
};

export default DeletePoolModal;
